import { useRef, useState } from "react";
import { NavLink, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useMiningData } from "api/mining";
import { useElectricityData } from "api/electricity";
import { useHeatData } from "api/heat";
import { useGasData } from "api/gas";

import OutputCombo from "components/OutputCombo";
import Doughnut from "components/ThermalEfficiency/Doughnut";

export default function ProjectEarnings() {
  const { user } = useAuth0();

  const [farmName, dayRange] = useOutletContext<any>();

  const { data: miningData } = useMiningData(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"], dayRange, farmName);
  const { data: electricityData } = useElectricityData(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"], dayRange, farmName);
  const { data: heatData } = useHeatData(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"], dayRange, farmName);
  const { data: gasData } = useGasData(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"], dayRange, farmName);

  const { eurosImportedFromGrid = 0, eurosSoldToGrid = 0, buyRevenue = 0 } = electricityData?.total ?? {};
  const { eurosMined = 0 } = miningData?.total ?? {};
  const { heatByCHP = 0, heatByMiners = 0 } = heatData?.total ?? {};
  const { eurosGasConsumed = 0, price = 0 } = gasData?.total ?? {};

  const thermalSales = (heatByCHP + heatByMiners) / 8790 * price;
  const totalEarnings = eurosMined + eurosSoldToGrid + buyRevenue + thermalSales;
  const totalCost = eurosGasConsumed + eurosImportedFromGrid;

  const svgRef = useRef(null);
  const [width, setWidth] = useState(300);
  const [height, setHeight] = useState(300);

  return (
    <>
      <div className="mt-5 px-5 lg:px-12 max-w-sm md:max-w-7xl mx-auto grid gap-5 md:grid-cols-2 lg:grid-cols-3 items-start font-['Avenir']">
        <div className="col-span-2 output-card text-gray-700">
          <div className="flex-shrink-0 mb-3 pt-7 px-7 text-xl font-bold">Total Project Earnings</div>
          <div className="mx-7 py-5 px-7 bg-gray-50">
            <div className="flex justify-between">
              <div>Project Earnings (Gross):</div>
              <div className="text-blue-600 font-bold text-2xl">{totalEarnings.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
            </div>
            <div className="border-l mt-2 pt-2 pl-5 space-y-4">
              <div className="flex justify-between">
                <div className="text-xs">Bitcoin earnings</div>
                <div className="text-lg text-gray-400">{eurosMined.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">Thermal sales</div>
                <div className="text-lg text-gray-400">{thermalSales.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">Electricity sold to grid</div>
                <div className="text-lg text-gray-400">{eurosSoldToGrid.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">Load balancing earnings</div>
                <div className="text-lg text-gray-400">{buyRevenue.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
            </div>
          </div>
          <div className="my-4 mx-7 py-5 px-7 bg-gray-50">
            <div className="flex justify-between">
              <div>Project Costs:</div>
              <div className="text-cyan-600 font-bold text-2xl">{totalCost.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
            </div>
            <div className="border-l mt-2 pt-2 pl-5 space-y-4">
              <div className="flex justify-between">
                <div className="text-xs">Gas Costs</div>
                <div className="text-lg text-gray-400">{eurosGasConsumed.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
              <div className="flex justify-between">
                <div className="text-xs">Other Costs</div>
                <div className="text-lg text-gray-400">{eurosImportedFromGrid.toLocaleString("en-GB", { style: "currency", currency: "EUR" })}</div>
              </div>
            </div>
          </div>
          <div className="py-8 px-14 mt-3 bg-slate-100 border-t border-gray-100 text-gray-700">
            <div className="flex justify-between items-center">
              <div className="font-bold text-2xl">Project Revenue (Net):</div>
              <div className="font-[Avenir-Bold] text-4xl text-blue-600">
                {(totalEarnings - totalCost).toLocaleString("en-GB", { style: "currency", currency: "EUR" })}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-xs">Your Profit Share (Net):</div>
              <div className="text-lg text-slate-400 font-sans">€268,545.64 (37%)</div>
            </div>
          </div>
        </div>
        <div className="space-y-5">
          <div className="output-card">
            <div className="flex-shrink-0 mb-3 pt-7 px-7 text-xl text-gray-700 font-bold">Project Share Split</div>
            <div className="flex flex-col items-center">
              <div className="h-48 w-48 relative">
                <svg
                  overflow="visible"
                  className="cursor-crosshair"
                  ref={(node) => {
                    if (node) {
                      const rect = node.getBoundingClientRect();
                      setWidth(rect.width);
                      setHeight(rect.height);
                      svgRef.current = node;
                    }
                  }}
                  viewBox={`0 0 ${width} ${height}`}
                >
                  <g transform="translate(10, 10)">
                    <Doughnut
                      x={(width - 20) / 2}
                      y={(height - 20) / 2}
                      innerRadius={Math.min(width - 20, height - 20) / 2 - 45}
                      outerRadius={Math.min(width - 20, height - 20) / 2}
                      color="#5676e9"
                      secondColor="#2ac0d5"
                      value={50}
                      cornerRadius={0}
                      // onMouseOver={handleMouseOver}
                      // onMouseMove={handleMouseMove}
                      // onMouseOut={handleMouseOut}
                    />
                  </g>
                </svg>
              </div>
              <div className="w-3/5 flex flex-wrap justify-between align-middle gap-2 my-4 ml-5 text-xs">
                <div className="flex">
                  <div className="rounded-full bg-cyan-600 w-4 h-4 mr-2"></div>
                  <div className="shrink">Company A</div>
                </div>
                <div className="flex">
                  <div className="rounded-full bg-blue-600 w-4 h-4 mr-2"></div>
                  <div className="shrink">Company B</div>
                </div>
              </div>
            </div>
          </div>
          <div className="output-card">
            <div className="flex-shrink-0 mb-3 pt-7 px-7 text-xl text-gray-700 font-bold">
              Energy Price (per m<sup>2</sup>)
            </div>
            <OutputCombo
              title="Total Thermal Costs"
              info="Some Info about"
              topValue={(gasData ? gasData.total.eurosGasConsumed : 0).toLocaleString("en-GB", { style: "currency", currency: "EUR" })}
              // smallValue={`${data ? data.consumed.toFixed(0) : 0} m3`}
              className="px-7"
              small
            />
            <OutputCombo
              title="Revenue Profit (Net)"
              info="Some Info about"
              topValue={`€299,447.03`}
              // smallValue={`450,000 m3`}
              className="mt-1 px-7"
              small
            />
            <OutputCombo
              title="Energy Price (Net)"
              info="Some Info about"
              topValue={`€299,447.03`}
              // smallValue={`450,000 m3`}
              className="mt-5 px-7"
              small
            />
            <OutputCombo
              title="Glass House Surface"
              info="Some Info about"
              topValue={`10,447m2`}
              // smallValue={`450,000 m3`}
              className="mt-1 px-7"
              small
            />
            <div className="py-5 px-7 mt-3 bg-slate-100 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <div className="font-bold text-xs">Avg. Heating Price</div>
                <div className="text-blue-600">
                  <span className="font-bold text-lg">€30</span>
                  <span className="text-xs">
                    {" "}
                    (per m<sup>2</sup>)
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="font-bold text-xs">Cost Price</div>
                <div className="text-blue-600">
                  <span className="font-bold text-lg">€8</span>
                  <span className="text-xs">
                    {" "}
                    (per m<sup>2</sup>)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap justify-between px-5 lg:px-12 max-w-sm md:max-w-7xl mx-auto text-gray-500 font-['Avenir']">
        <NavLink to="/dashboard" className="text-blue-600 mr-5 text-sm flex items-center">
          <code className="text-3xl mr-2">&lt;</code> <div className="mt-1">Back to dashboard</div>
        </NavLink>
      </div>
    </>
  );
}
